import React, { useMemo, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { ROUTE } from "@/route/app_routes.js";
import { resetPassword } from "@/services/authService";
import { getErrorMessage } from "@/services/apiClient";

const NewPasswordForm = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { token, email } = router.query;
  const [focusedInput, setFocusedInput] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState(null);

  const newPasswordSchema = useMemo(
    () =>
      z
        .object({
          password: z.string().min(8, t("auth.validation.passwordMinLength")),
          password_confirmation: z.string().min(1, t("auth.validation.confirmPasswordRequired")),
        })
        .refine((data) => data.password === data.password_confirmation, {
          message: t("auth.validation.passwordsDontMatch"),
          path: ["password_confirmation"],
        }),
    [t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(newPasswordSchema),
  });

  const onSubmit = async (values) => {
    setServerError(null);

    if (!token || !email) {
      const message = t("auth.resetPassword.invalidLinkError");
      setServerError(message);
      toast.error(message);
      return;
    }

    try {
      await resetPassword({ ...values, token, email });
      toast.success(t("auth.resetPassword.successToast"));
      router.push(ROUTE.login);
    } catch (error) {
      const message = getErrorMessage(error);
      setServerError(message);
      toast.error(message);
    }
  };

  return (
    <div className="rbt-auth-split__form-card">
      <div className="rbt-auth-split__welcome">
        <h1 className="title">{t("auth.resetPassword.title")}</h1>
        <p>{t("auth.resetPassword.subheading")}</p>
      </div>

      {serverError && (
        <div className="rbt-auth-split__alert" role="alert">
          <i className="feather-alert-circle"></i>
          <span>{serverError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="rbt-auth-split__field">
          <div className={`form-group rbt-password-group ${focusedInput === "password" ? "focused" : ""}`}>
            <i className="feather-lock rbt-auth-split__field-icon"></i>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder=" "
              autoComplete="new-password"
              {...register("password")}
              onFocus={() => setFocusedInput("password")}
              onBlur={(e) => { setFocusedInput(null); register("password").onBlur(e); }}
            />
            <label htmlFor="password">{t("auth.resetPassword.newPassword")}</label>
            <button
              type="button"
              className="rbt-password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              <i className={showPassword ? "feather-eye-off" : "feather-eye"}></i>
            </button>
            <span className="focus-border"></span>
          </div>
          {errors.password && <span className="text-danger d-block mt--5">{errors.password.message}</span>}
        </div>

        <div className="rbt-auth-split__field">
          <div className={`form-group rbt-password-group ${focusedInput === "password_confirmation" ? "focused" : ""}`}>
            <i className="feather-lock rbt-auth-split__field-icon"></i>
            <input
              id="password_confirmation"
              type={showConfirmPassword ? "text" : "password"}
              placeholder=" "
              autoComplete="new-password"
              {...register("password_confirmation")}
              onFocus={() => setFocusedInput("password_confirmation")}
              onBlur={(e) => { setFocusedInput(null); register("password_confirmation").onBlur(e); }}
            />
            <label htmlFor="password_confirmation">{t("auth.resetPassword.confirmNewPassword")}</label>
            <button
              type="button"
              className="rbt-password-toggle"
              onClick={() => setShowConfirmPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showConfirmPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              <i className={showConfirmPassword ? "feather-eye-off" : "feather-eye"}></i>
            </button>
            <span className="focus-border"></span>
          </div>
          {errors.password_confirmation && <span className="text-danger d-block mt--5">{errors.password_confirmation.message}</span>}
        </div>

        <div className="form-submit-group mt--10">
          <button type="submit" className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100" disabled={isSubmitting}>
            <span className="icon-reverse-wrapper">
              <span className="btn-text">
                {isSubmitting && <span className="rbt-auth-split__spinner" aria-hidden="true" />}
                {isSubmitting ? t("auth.resetPassword.resetting") : t("auth.resetPassword.resetPassword")}
              </span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
            </span>
          </button>
        </div>

        <p className="rbt-auth-split__legal">
          {t("auth.rememberedPassword")} <Link href={ROUTE.login}>{t("auth.signIn")}</Link>
        </p>
      </form>
    </div>
  );
};

export default NewPasswordForm;
